import "./CanvasContentErrorBoundary.scss";
import * as React from "react";
import { connect } from "react-redux";
import { RootState } from "../../store";
import { AppState } from "../../store/app/types";
import { metastoreSupportsStableRedirect } from "../metastore-api-client";

type CanvasContentErrorBoundaryProps = React.PropsWithChildren<{
    appState: AppState;
}>;

type CanvasContentErrorBoundaryState = {
    error: unknown;
    errorInfo: React.ErrorInfo;
    redirectMessage?: string;
    appState: AppState;
};

class CanvasContentErrorBoundary extends React.Component<CanvasContentErrorBoundaryProps, CanvasContentErrorBoundaryState> {
    constructor(props: CanvasContentErrorBoundaryProps) {
        super(props);
        this.state = { error: null, errorInfo: null, appState: props.appState };
    }

    getRedirectInformation() {
        // Short-circuit if the current user is a developer, no redirect is configured for this environment or if
        // the Metastore does not support CORS calls from the stable subdomain yet...
        const { isDeveloper } = this.state.appState.user;
        const { redirectOnError } = this.state.appState.settings;
        if (
            isDeveloper ||
            !redirectOnError || !redirectOnError.urlRewriteRules?.length ||
            !metastoreSupportsStableRedirect()
        ) return {};

        return {
            message: redirectOnError.message,
            // Apply all rewrite rules to the current URL...
            url: redirectOnError.urlRewriteRules.reduce(
                (acc, elem) => acc.replace(new RegExp(elem.match), elem.replacement),
                window.location.href
            )
        };
    }

    componentDidCatch(error: unknown, errorInfo: React.ErrorInfo) {
        // If an automatic redirect is configured, display the configured message and redirect to the calculated URL shortly afterward...
        const { message: redirectMessage, url: redirectUrl } = this.getRedirectInformation();
        if (redirectUrl) {
            setTimeout(() => window.location.href = redirectUrl, 500);
        }

        this.setState({
            redirectMessage,
            error: error,
            errorInfo: errorInfo
        });
    }

    render() {
        if (this.state.errorInfo) {
            return (
                <div className="screen-centered canvas-content-error-boundary">
                    <h2 className="header">{ this.state.redirectMessage ?? "Failed to Render Canvas." }</h2>
                    <details className="error-details">
                        <pre>
                            <span className="exception">{this.state.error?.toString()}</span>
                            <br />
                            <span className="stack-trace">{this.state.errorInfo.componentStack}</span>
                        </pre>
                    </details>
                </div>
            );
        }

        // Normally, just render children...
        return this.props.children;
    }
}

export default connect(
    (state: RootState) => ({
        appState: state.app
    })
)(CanvasContentErrorBoundary);